import React, { useState, useRef, useEffect } from 'react';
import { Trash2, Copy, RotateCcw, Code, Plus, ArrowRight, Grid, List, Edit3 } from 'lucide-react';
import "../../assets/css/CreateYourOwn/CommonCreate.css"
import "../../assets/css/CreateYourOwn/arrayBuilder.css"
import { useDarkMode } from 'context/DarkModeProvider';
import { useLanguage } from 'context/LanguageProvider';
import CodeHighlighter from 'components/CodeHighlighter';

const ArrayBuilder = () => {
    const [array, setArray] = useState({
        id: 1,
        name: 'My Array',
        elements: [],
        type: '1D',
        rows: 1,
        cols: 1
    });
    const [newElement, setNewElement]               = useState('');
    const [animatingOperation, setAnimatingOperation] = useState(null);
    const [codeLanguage, setCodeLanguage]           = useState('javascript');
    const [showInfo, setShowInfo]                   = useState(false);
    const [operationResult, setOperationResult]     = useState(null);
    const [persistentResult, setPersistentResult]   = useState(null);
    const [arrayType, setArrayType]                 = useState('1D');
    // Store raw string values so backspace works freely
    const [dimRows, setDimRows]                     = useState('1');
    const [dimCols, setDimCols]                     = useState('4');
    const [editingIndex, setEditingIndex]           = useState(null);
    const containerRef                              = useRef(null);
    const [copied, setCopied]                       = useState(false);
    const [counter, setCounter]                     = useState(1);
    const [actionLog, setActionLog]                 = useState([]);

    const { darkMode }    = useDarkMode();
    const { selLanguage } = useLanguage();

    // ── helpers ───────────────────────────────────────────────────────────────
    const animateOperation = (operation, result = null) => {
        setAnimatingOperation({ operation });
        if (result) {
            setOperationResult(result);
            setPersistentResult(result);
            setTimeout(() => setOperationResult(null), 800);
        }
        setTimeout(() => setAnimatingOperation(null), 600);
    };

    // ── switch 1D / 2D ────────────────────────────────────────────────────────
    const switchArrayType = (newType) => {
        setArrayType(newType);
        const rows = Math.max(1, parseInt(dimRows) || 1);
        const cols = Math.max(1, parseInt(dimCols) || 1);
        if (newType === '1D') {
            setArray(prev => ({ ...prev, type: '1D', elements: [], rows: 1, cols: 1 }));
        } else {
            setArray(prev => ({
                ...prev,
                type: '2D',
                elements: Array(rows).fill(null).map(() => Array(cols).fill('')),
                rows,
                cols
            }));
        }
        setActionLog(prev => [...prev, { type: 'switchType', newType }]);
        if (newType === '2D') setPersistentResult(null);
    };

    // ── 2D dimension change — fires only on valid committed value ─────────────
    const applyDimensions = (rawRows, rawCols) => {
        const rows = Math.min(10, Math.max(1, parseInt(rawRows) || 1));
        const cols = Math.min(10, Math.max(1, parseInt(rawCols) || 1));
        if (arrayType === '2D') {
            setArray(prev => ({
                ...prev,
                elements: Array(rows).fill(null).map(() => Array(cols).fill('')),
                rows,
                cols
            }));
        }
    };

    const handleRowsChange = (e) => {
        const raw = e.target.value;
        setDimRows(raw);                      // allow empty / partial typing
        if (raw !== '' && !isNaN(raw)) applyDimensions(raw, dimCols);
    };

    const handleColsChange = (e) => {
        const raw = e.target.value;
        setDimCols(raw);
        if (raw !== '' && !isNaN(raw)) applyDimensions(dimRows, raw);
    };

    const handleRowsBlur = () => {
        const safe = String(Math.min(10, Math.max(1, parseInt(dimRows) || 1)));
        setDimRows(safe);
        applyDimensions(safe, dimCols);
    };

    const handleColsBlur = () => {
        const safe = String(Math.min(10, Math.max(1, parseInt(dimCols) || 1)));
        setDimCols(safe);
        applyDimensions(dimRows, safe);
    };

    // ── 1D add ────────────────────────────────────────────────────────────────
    const addElementTo1D = () => {
        const element = newElement.trim() ? newElement.trim() : `Item${counter}`;
        if (!newElement.trim()) setCounter(p => p + 1);
        setArray(prev => ({ ...prev, elements: [...prev.elements, element] }));
        setNewElement('');
        animateOperation('addElement');
        setActionLog(prev => [...prev, { type: 'addElement', value: element }]);
    };

    // ── 2D cell update ────────────────────────────────────────────────────────
    const updateElementIn2D = (row, col, value) => {
        setArray(prev => {
            const newElements = prev.elements.map(r => [...r]);
            newElements[row][col] = value;
            return { ...prev, elements: newElements };
        });
        setActionLog(prev => [...prev, { type: 'set2D', row, col, value }]);
    };

    // ── remove 1D ─────────────────────────────────────────────────────────────
    const removeElement = (index) => {
        const val = array.elements[index];
        setArray(prev => ({ ...prev, elements: prev.elements.filter((_, i) => i !== index) }));
        animateOperation('removeElement');
        setActionLog(prev => [...prev, { type: 'removeElement', value: val, index }]);
    };

    // ── clear ─────────────────────────────────────────────────────────────────
    const clearArray = () => {
        if (array.type === '1D') {
            setArray(prev => ({ ...prev, elements: [] }));
        } else {
            setArray(prev => ({
                ...prev,
                elements: Array(prev.rows).fill(null).map(() => Array(prev.cols).fill(''))
            }));
        }
        animateOperation('clear');
        setActionLog(prev => [...prev, { type: 'clear' }]);
    };

    // ── reverse ───────────────────────────────────────────────────────────────
    const performReverse = () => {
        if (array.type !== '1D' || array.elements.length === 0) return;
        const reversed = [...array.elements].reverse();
        const result = { operation: 'reverse', elements: reversed, arrays: [array.name] };
        setPersistentResult(result);
        setActionLog(prev => [...prev, { type: 'reverse' }]);
    };

    // ── code generation (actionLog) ───────────────────────────────────────────
    const generateCode = () => {
        const varJs  = array.name.replace(/\s+/g, '').toLowerCase();
        const varPy  = array.name.replace(/\s+/g, '_').toLowerCase();

        const renderActions = (lang) => actionLog.map(action => {
            if (action.type === 'addElement') {
                if (lang === 'js')   return `    ${varJs}.push("${action.value}");`;
                if (lang === 'py')   return `    ${varPy}.append("${action.value}")`;
                if (lang === 'java') return `        ${varJs}.add("${action.value}");`;
                if (lang === 'cpp')  return `    ${varPy}.push_back("${action.value}");`;
            }
            if (action.type === 'removeElement') {
                if (lang === 'js')   return `    ${varJs}.splice(${action.index}, 1); // removed "${action.value}"`;
                if (lang === 'py')   return `    ${varPy}.remove("${action.value}")`;
                if (lang === 'java') return `        ${varJs}.remove("${action.value}");`;
                if (lang === 'cpp')  return `    ${varPy}.erase(${varPy}.begin() + ${action.index}); // "${action.value}"`;
            }
            if (action.type === 'set2D') {
                if (lang === 'js')   return `    ${varJs}[${action.row}][${action.col}] = "${action.value}";`;
                if (lang === 'py')   return `    ${varPy}[${action.row}][${action.col}] = "${action.value}"`;
                if (lang === 'java') return `        ${varJs}[${action.row}][${action.col}] = "${action.value}";`;
                if (lang === 'cpp')  return `    ${varPy}[${action.row}][${action.col}] = "${action.value}";`;
            }
            if (action.type === 'reverse') {
                if (lang === 'js')   return `    ${varJs}.reverse();`;
                if (lang === 'py')   return `    ${varPy}.reverse()`;
                if (lang === 'java') return `        Collections.reverse(${varJs});`;
                if (lang === 'cpp')  return `    reverse(${varPy}.begin(), ${varPy}.end());`;
            }
            if (action.type === 'clear') {
                if (lang === 'js')   return `    // Array cleared at this point`;
                if (lang === 'py')   return `    # Array cleared at this point`;
                if (lang === 'java') return `        // Array cleared at this point`;
                if (lang === 'cpp')  return `    // Array cleared at this point`;
            }
            return '';
        }).filter(Boolean).join('\n');

        // ── 2D snapshot ───────────────────────────────────────────────────────
        if (arrayType === '2D') {
            const mat = JSON.stringify(array.elements);
            const matPy = mat.replace(/"/g, "'");

            const codes2D = {
                javascript: `// 2D Array in JavaScript

function main() {
    const ${varJs} = ${mat};

    console.log("Rows:", ${varJs}.length);
    console.log("Cols:", ${varJs}[0]?.length ?? 0);
    console.log("Matrix:");
    ${varJs}.forEach((row, i) => console.log(\`  [\${i}]: \${row.join(", ")}\`));
}

main();`,

                python: `# 2D Array in Python

def main():
    ${varPy} = ${matPy}

    print(f"Rows: {len(${varPy})}")
    print(f"Cols: {len(${varPy}[0]) if ${varPy} else 0}")
    print("Matrix:")
    for i, row in enumerate(${varPy}):
        print(f"  [{i}]: {row}")

if __name__ == "__main__":
    main()`,

                java: `// 2D Array in Java
import java.util.Arrays;

public class Main {
    public static void main(String[] args) {
        String[][] ${varJs} = ${
    mat.replace(/\[\[/g, '{{').replace(/\]\]/g, '}}')
       .replace(/\],\[/g, '},{').replace(/"/g, '"')
};

        System.out.println("Rows: " + ${varJs}.length);
        System.out.println("Cols: " + ${varJs}[0].length);
        for (int i = 0; i < ${varJs}.length; i++) {
            System.out.println("  [" + i + "]: " + Arrays.toString(${varJs}[i]));
        }
    }
}`,

                cpp: `// 2D Array in C++
#include <iostream>
#include <vector>
#include <string>
using namespace std;

int main() {
    vector<vector<string>> ${varPy} = ${
    mat.replace(/\[/g, '{').replace(/\]/g, '}')
};

    cout << "Rows: " << ${varPy}.size() << endl;
    cout << "Cols: " << (${varPy}.empty() ? 0 : (int)${varPy}[0].size()) << endl;
    for (int i = 0; i < (int)${varPy}.size(); i++) {
        cout << "  [" << i << "]: ";
        for (const auto& c : ${varPy}[i]) cout << c << " ";
        cout << endl;
    }
    return 0;
}`,
            };
            return codes2D[codeLanguage] || '';
        }

        // ── 1D actionLog ──────────────────────────────────────────────────────
        const codes = {
            javascript: `// 1D Array in JavaScript

class ArrayOperations {
    static slice(arr, start = 0, end = arr.length) { return arr.slice(start, end); }
    static reverse(arr)           { return [...arr].reverse(); }
    static map(arr, cb)           { return arr.map(cb); }
    static filter(arr, cb)        { return arr.filter(cb); }
    static indexOf(arr, el)       { return arr.indexOf(el); }
    static includes(arr, el)      { return arr.includes(el); }
}

// Main
function main() {
    const ${varJs} = [];
${renderActions('js')}
    console.log(${varJs});
    console.log("Length:", ${varJs}.length);
    ${array.elements.length > 0 ? `console.log("First element:", ${varJs}[0]);` : '// Add elements to see examples'}
}

main();`,

            python: `# 1D Array in Python

class ArrayOperations:
    @staticmethod
    def slice_array(arr, start=0, end=None): return arr[start:end]
    @staticmethod
    def reverse(arr):                        return arr[::-1]
    @staticmethod
    def filter_array(arr, func):             return [x for x in arr if func(x)]
    @staticmethod
    def index_of(arr, el):                   return arr.index(el) if el in arr else -1

# Main
if __name__ == "__main__":
    ${varPy} = []
${renderActions('py')}
    print(${varPy})
    print(f"Length: {len(${varPy})}")
    ${array.elements.length > 0 ? `print(f"First element: {${varPy}[0]}")` : '# Add elements to see examples'}`,

            java: `// 1D Array in Java
import java.util.ArrayList;
import java.util.Collections;

public class Main {
    public static void main(String[] args) {
        ArrayList<String> ${varJs} = new ArrayList<>();
${renderActions('java')}
        System.out.println(${varJs});
        System.out.println("Length: " + ${varJs}.size());
        ${array.elements.length > 0 ? `System.out.println("First element: " + ${varJs}.get(0));` : '// Add elements to see examples'}
    }
}`,

            cpp: `// 1D Array in C++
#include <iostream>
#include <vector>
#include <string>
#include <algorithm>
using namespace std;

int main() {
    vector<string> ${varPy};
${renderActions('cpp')}
    cout << "Length: " << ${varPy}.size() << endl;
    ${array.elements.length > 0 ? `cout << "First element: " << ${varPy}[0] << endl;` : '// Add elements to see examples'}
    for (const auto& el : ${varPy}) cout << el << " ";
    cout << endl;
    return 0;
}`,
        };

        return codes[codeLanguage] || '';
    };

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div
            className='commonContainerCreate'
            style={{ background: darkMode ? "#0F172A" : "var(--lightModeBack)" }}
        >
            <div
                className='commonVisual'
                style={{
                    background: darkMode ? '#1E293B' : 'var(--customBuildBack)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderColor: darkMode ? 'rgba(122, 142, 255, 0.3)' : 'rgba(0, 0, 0, 0.2)',
                }}
            >
                <h1 className='commonCreateTitle'>
                    {selLanguage ? "Δημιουργία Πίνακα" : "Array Builder"}
                </h1>

                {/* ── Array Type Selector ───────────────────────────────────── */}
                <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', justifyContent: 'center' }}>
                    <button
                        onClick={() => switchArrayType('1D')}
                        className='commonButtonCreate'
                        style={{
                            background: arrayType === '1D' ? "#7a8effff" : darkMode ? "#1e293b" : "var(--customBuildButton)",
                            color: arrayType === '1D' ? "white" : "#7a8effff",
                            border: "2px solid #7a8effff",
                            padding: '12px 20px'
                        }}
                    >
                        <List size={16} style={{ marginRight: '8px' }} />
                        {selLanguage ? "Μονοδιάστατος Πίνακας" : "1D Array"}
                    </button>
                    <button
                        onClick={() => switchArrayType('2D')}
                        className='commonButtonCreate'
                        style={{
                            background: arrayType === '2D' ? "#7a8effff" : darkMode ? "#1e293b" : "var(--customBuildButton)",
                            color: arrayType === '2D' ? "white" : "#7a8effff",
                            border: "2px solid #7a8effff",
                            padding: '12px 20px'
                        }}
                    >
                        <Grid size={16} style={{ marginRight: '8px' }} />
                        {selLanguage ? "Δυσδιάστατος Πίνακας" : "2D Array"}
                    </button>
                </div>

                {/* ── 2D Dimensions — FIXED backspace ──────────────────────── */}
                {arrayType === '2D' && (
                    <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', justifyContent: 'center' }}>
                        <div>
                            <label style={{ color: darkMode ? 'white' : '#111', fontSize: '0.9rem', marginRight: '8px' }}>Rows:</label>
                            <input
                                type="number"
                                min="1"
                                max="10"
                                value={dimRows}
                                onChange={handleRowsChange}
                                onBlur={handleRowsBlur}
                                style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', width: '60px' }}
                            />
                        </div>
                        <div>
                            <label style={{ color: darkMode ? 'white' : '#111', fontSize: '0.9rem', marginRight: '8px' }}>Cols:</label>
                            <input
                                type="number"
                                min="1"
                                max="10"
                                value={dimCols}
                                onChange={handleColsChange}
                                onBlur={handleColsBlur}
                                style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', width: '60px' }}
                            />
                        </div>
                    </div>
                )}

                {/* ── 1D Input ──────────────────────────────────────────────── */}
                {arrayType === '1D' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '15px', marginBottom: '20px' }}>
                        <input
                            type="text"
                            value={newElement}
                            onChange={(e) => setNewElement(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && addElementTo1D()}
                            placeholder={selLanguage ? "Πρόσθεσε στοιχείο στον πίνακα..." : "Add element to array..."}
                            style={{
                                padding: '16px 20px', border: '2px solid #e5e7eb', borderRadius: '16px',
                                fontSize: '1rem', background: 'white', fontFamily: 'inherit',
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#667eea'}
                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                            className="custom-input"
                        />
                        <style>{`.custom-input::placeholder { color: #363a42ff; opacity: 1; }`}</style>

                        <div style={{ position: 'relative', display: 'inline-block' }}>
                            <button
                                className='commonButtonCreate'
                                style={{
                                    background: darkMode ? "#1e293b" : "var(--customBuildButton)",
                                    color: "#7a8effff", border: "2px solid #7a8effff", height: '100%',
                                }}
                                onMouseEnter={() => setShowInfo(true)}
                                onMouseLeave={() => setShowInfo(false)}
                            >💡</button>
                            {showInfo && (
                                <div style={{
                                    position: 'absolute', top: '100%', right: '0', marginTop: '10px',
                                    padding: '20px',
                                    background: 'linear-gradient(135deg, rgba(30,41,59,0.98), rgba(51,65,85,0.98))',
                                    backdropFilter: 'blur(20px)', borderRadius: '16px',
                                    border: '1px solid rgba(122,142,255,0.3)',
                                    boxShadow: '0 20px 40px rgba(0,0,0,0.3)', zIndex: 1000, minWidth: '280px'
                                }}>
                                    <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#fff', marginBottom: '15px', textAlign: 'center' }}>
                                        {selLanguage ? "Οδηγίες" : "Instructions"}
                                    </h3>
                                    <div style={{ fontSize: '0.85rem', color: '#cbd5e1', lineHeight: '1.6' }}>
                                        <div style={{ marginBottom: '8px' }}><strong style={{ color: '#7a8eff' }}>Click element:</strong> {selLanguage ? "Αφαίρεση από 1D πίνακα" : "Remove from 1D array"}</div>
                                        <div style={{ marginBottom: '8px' }}><strong style={{ color: '#7a8eff' }}>Click cell:</strong> {selLanguage ? "Επεξεργασία στοιχείου 2D πίνακα" : "Edit 2D array element"}</div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Operation Buttons ─────────────────────────────────────── */}
                <div className={`opButtons lang-${selLanguage ? "el" : "en"}`}
                    style={{
                        display: 'grid',
                        gridTemplateColumns: arrayType === '1D' ? 'repeat(3, 1fr)' : '1fr',
                        gap: '15px', marginBottom: '20px'
                    }}>
                    {arrayType === '1D' && (
                        <>
                            <button onClick={addElementTo1D} className='commonButtonCreate'
                                style={{ background: darkMode ? "#1e293b" : "var(--customBuildButton)", color: "#7a8effff", border: "2px solid #7a8effff" }}>
                                <Plus size={16} style={{ marginRight: '8px' }} />
                                {selLanguage ? "Προσθήκη στοιχείου" : "Add Element"}
                            </button>
                            <button onClick={performReverse} disabled={!array.elements.length} className='commonButtonCreate'
                                style={{ background: darkMode ? "#1e293b" : "var(--customBuildButton)", color: "#7a8effff", border: "2px solid #7a8effff", cursor: !array.elements.length ? 'not-allowed' : 'pointer' }}>
                                ↔ {selLanguage ? "Αντιστροφή" : "Reverse"}
                            </button>
                        </>
                    )}
                    <button onClick={clearArray} className='commonButtonCreate'
                        style={{ background: darkMode ? "#1e293b" : "var(--customBuildButton)", color: "#7a8effff", border: "2px solid #7a8effff" }}>
                        <RotateCcw size={16} style={{ marginRight: '8px' }} />
                        {selLanguage ? "Καθαρισμός" : "Restart"}
                    </button>
                </div>

                {/* ── Reverse Result ────────────────────────────────────────── */}
                {persistentResult && (
                    <div style={{
                        background: 'linear-gradient(135deg, rgba(102,126,234,0.1), rgba(118,75,162,0.1))',
                        border: '2px solid rgba(102,126,234,0.3)', borderRadius: '16px',
                        padding: '20px', marginBottom: '20px', position: 'relative'
                    }}>
                        <button onClick={() => setPersistentResult(null)} style={{
                            position: 'absolute', top: '10px', right: '10px',
                            background: 'rgba(239,68,68,0.8)', border: 'none', borderRadius: '50%',
                            width: '24px', height: '24px', color: 'white', cursor: 'pointer',
                            fontSize: '12px', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>×</button>
                        <div style={{ fontSize: '1.2rem', fontWeight: '700', color: 'white', marginBottom: '12px', textTransform: 'capitalize' }}>
                            {persistentResult.operation === 'reverse' && '↔ Reverse'}
                            {' '}Result: {persistentResult.arrays.join(' + ')}
                            {persistentResult.info && ` ${persistentResult.info}`}
                        </div>
                        <div style={{ background: 'rgba(255,255,255,0.15)', padding: '12px', borderRadius: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px', minHeight: '40px', alignItems: 'center' }}>
                            {persistentResult.elements.length === 0
                                ? <span style={{ color: 'white', fontStyle: 'italic' }}>Empty array []</span>
                                : persistentResult.elements.map((elem, i) => (
                                    <span key={i} style={{ background: 'rgba(255,255,255,0.25)', color: 'white', padding: '6px 12px', borderRadius: '8px', fontSize: '0.9rem', fontWeight: '500', border: '2px solid rgba(255,255,255,0.3)' }}>
                                        [{i}] {elem}
                                    </span>
                                ))
                            }
                        </div>
                    </div>
                )}

                {/* ── Array Visualization ───────────────────────────────────── */}
                <div className='arrayVisualCreate' style={{ border: !darkMode ? '1px solid black' : 'none' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#667eea', marginBottom: '20px', textAlign: 'center' }}>
                        {array.name}
                        <div style={{ fontSize: '0.9rem', opacity: 0.8, fontWeight: '500' }}>
                            {array.type} - {array.type === '1D' ? `${array.elements.length} elements` : `${array.rows}×${array.cols} matrix`}
                        </div>
                    </div>

                    {array.type === '1D' ? (
                        <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '8px', minHeight: '60px', alignItems: 'center', justifyContent: array.elements.length === 0 ? 'center' : 'flex-start', overflowX: 'auto', overflowY: 'hidden', width: '100%', paddingBottom: '10px' }}>
                            {array.elements.length === 0
                                ? <div style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '1.1rem', textAlign: 'center' }}>Empty array []</div>
                                : array.elements.map((element, index) => (
                                    <div key={index} onClick={(e) => { e.stopPropagation(); removeElement(index); }}
                                        className='oneDVisual' title="Click to remove"
                                        onMouseEnter={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 6px 16px rgba(102,126,234,0.4)'; }}
                                        onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 4px 12px rgba(102,126,234,0.3)'; }}
                                    >
                                        [{index}] {element}
                                        <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>×</span>
                                    </div>
                                ))
                            }
                        </div>
                    ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${array.cols}, 1fr)`, gap: '8px', minHeight: '120px' }}>
                            {array.elements.map((row, rowIndex) =>
                                row.map((cell, colIndex) => (
                                    <div key={`${rowIndex}-${colIndex}`}
                                        onClick={(e) => { e.stopPropagation(); setEditingIndex(`${rowIndex}-${colIndex}`); }}
                                        style={{ background: '#667eea', color: 'white', padding: '16px', borderRadius: '12px', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', border: '3px solid #667eea', transition: 'all 0.2s ease', textAlign: 'center', minHeight: '50px', minWidth: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(102,126,234,0.3)' }}
                                        title={`[${rowIndex}][${colIndex}] - Click to edit`}
                                        onMouseEnter={(e) => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 6px 16px rgba(102,126,234,0.4)'; }}
                                        onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 4px 12px rgba(102,126,234,0.3)'; }}
                                    >
                                        {editingIndex === `${rowIndex}-${colIndex}` ? (
                                            <input type="text" defaultValue={cell}
                                                style={{ background: 'transparent', border: 'none', color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit', textAlign: 'center', outline: 'none', width: '100%' }}
                                                autoFocus
                                                onBlur={(e) => { updateElementIn2D(rowIndex, colIndex, e.target.value); setEditingIndex(null); }}
                                                onKeyPress={(e) => { if (e.key === 'Enter') { updateElementIn2D(rowIndex, colIndex, e.target.value); setEditingIndex(null); } }}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        ) : (
                                            cell || `[${rowIndex}][${colIndex}]`
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {/* operation flash */}
                    {animatingOperation && (
                        <div className='arrayOperationCreate'>
                            {animatingOperation.operation === 'addElement'    && 'ADDING ELEMENT'}
                            {animatingOperation.operation === 'removeElement' && 'REMOVING ELEMENT'}
                            {animatingOperation.operation === 'clear'         && 'CLEARING ARRAY'}
                            {animatingOperation.operation === 'reverse'       && 'REVERSING ARRAY'}
                        </div>
                    )}
                </div>

                {/* ── Language Selector ─────────────────────────────────────── */}
                <div style={{ marginBottom: '20px' }}>
                    <label className='codeLabelCreate' style={{ color: darkMode ? "#fff" : "#0c0c0cff" }}>
                        {selLanguage ? "Γλώσσα Προγραμματισμού" : "Programming Language"}
                    </label>
                    <select className='codeSelectorCreate' value={codeLanguage} onChange={(e) => setCodeLanguage(e.target.value)}>
                        <option value="javascript">JavaScript</option>
                        <option value="python">Python</option>
                        <option value="java">Java</option>
                        <option value="cpp">C++</option>
                    </select>
                </div>

                {/* ── Code Display with CodeHighlighter ────────────────────── */}
                <div style={{
                    background: 'linear-gradient(145deg, #0f172a, #1e293b)',
                    borderRadius: '8px',
                    border: '1px solid #334155',
                    overflow: 'auto',
                    maxHeight: '300px',
                    fontSize: '0.75rem',
                }}>
                    <CodeHighlighter code={generateCode()} language={codeLanguage} />
                </div>

                {/* ── Copy Button ───────────────────────────────────────────── */}
                <button
                    onClick={() => { navigator.clipboard.writeText(generateCode()); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                    className='commonButtonCreate'
                    style={{
                        width: '100%', marginTop: '20px',
                        background: copied ? 'linear-gradient(135deg, #059669 0%, #047857 100%)' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        transform: copied ? 'translateY(-4px)' : 'translateY(0)',
                        transition: 'all 0.3s ease'
                    }}
                    onMouseOver={(e) => !copied && (e.target.style.transform = 'translateY(-2px)')}
                    onMouseOut={(e)  => !copied && (e.target.style.transform = 'translateY(0)')}
                >
                    <Copy size={16} style={{ marginRight: '8px' }} />
                    {copied ? (selLanguage ? ' Αντιγράφηκε!' : ' Copied') : (selLanguage ? ' Αντιγραφή' : ' Copy')}
                </button>
            </div>
        </div>
    );
};

export default ArrayBuilder;